import Vue from "vue";
import VueRouter from "vue-router";
import store from "../store";
import Parse from "../configs/parse";
import { getSession, getSessionToken } from "../utils/cookie";
import launch from "../utils/launcher";
import utils from "../utils";

Vue.use(VueRouter);

const Panel = () =>
  import(/* webpackChunkName: "panel" */ "../components/layouts/Panel.vue");
const Navigation = () =>
  import(
    /* webpackChunkName: "navigation" */ "../components/layouts/Navigation.vue"
  );
const Home = () =>
  import(/* webpackChunkName: "home" */ "../components/pages/Home.vue");
const Profile = () =>
  import(/* webpackChunkName: "profile" */ "../components/pages/Profile.vue");
const MyLoads = () =>
  import(/* webpackChunkName: "my-loads" */ "../components/pages/MyLoads.vue");
const DriverServices = () =>
  import(
    /* webpackChunkName: "driver-services" */ "../components/pages/DriverServices.vue"
  );
const FilterLoads = () =>
  import(
    /* webpackChunkName: "filter-loads" */ "../components/pages/FilterLoads.vue"
  );
const SingleLoad = () =>
  import(
    /* webpackChunkName: "single-load" */ "../components/pages/SingleLoad.vue"
  );
const CommentsList = () =>
  import(
    /* webpackChunkName: "comments-list" */ "../components/pages/CommentsList.vue"
  );
const LogisticLoad = () =>
  import(
    /* webpackChunkName: "logistic-load" */ "../components/pages/LogisticLoad.vue"
  );
const EditProfile = () =>
  import(
    /* webpackChunkName: "edit-profile" */ "../components/pages/EditProfile.vue"
  );
const Bookmarks = () =>
  import(
    /* webpackChunkName: "bookmarks" */ "../components/pages/Bookmark.vue"
  );
const Credit = () =>
  import(/* webpackChunkName: "credit" */ "../components/pages/Credit.vue");
const Wallet = () =>
  import(/* webpackChunkName: "wallet" */ "../components/pages/Wallet.vue");
const Transactions = () =>
  import(
    /* webpackChunkName: "transactions" */ "../components/pages/Transactions.vue"
  );
const CompleteProfile = () =>
  import(
    /* webpackChunkName: "complete-profile" */ "../components/pages/CompleteProfile.vue"
  );
const Support = () =>
  import(/* webpackChunkName: "support" */ "../components/pages/Support.vue");
const PaymentResult = () =>
  import(
    /* webpackChunkName: "payment-result" */ "../components/pages/PaymentResult.vue"
  );

const routes = [
  {
    path: "/",
    redirect: { name: "login" },
  },
  {
    path: "/login",
    name: "login",
    beforeEnter: async (to, from, next) => {
      const user = Parse.User.current();
      if (user && user.get("sessionToken") === getSessionToken()) {
        return next({ name: "home" });
      }

      const checked = await checkUser();
      if (checked) {
        await launch(false); // false: user fetched in become_user action

        next({ name: store.getters.redirect ?? "home" });
        store.commit("set_redirect", null);
      } else {
        utils.goToLoginProject();
      }
    },
  },
  {
    path: "/panel",
    component: Panel,
    children: [
      {
        path: "",
        component: Navigation,
        children: [
          {
            name: "home",
            path: "home",
            component: Home,
          },
          {
            name: "profile",
            path: "profile",
            component: Profile,
          },
          {
            name: "myLoads",
            path: "my-loads",
            component: MyLoads,
          },
          {
            name: "driverServices",
            path: "driver-services",
            component: DriverServices,
          },
          {
            name: "filter",
            path: "filter-loads",
            component: FilterLoads,
          },
        ],
      },
      {
        name: "singleLoad",
        path: "single-load/:loadId/:collection",
        component: SingleLoad,
      },
      {
        name: "commentsList",
        path: "load-comments/:loadownerId",
        component: CommentsList,
      },
      {
        name: "logisticLoad",
        path: "logistic-load/:loadId",
        component: LogisticLoad,
      },
      {
        name: "editProfile",
        path: "edit-profile",
        component: EditProfile,
      },
      {
        name: "bookmarks",
        path: "bookmarks",
        component: Bookmarks,
      },
      {
        name: "credit",
        path: "credit",
        component: Credit,
      },
      {
        name: "paymentResult",
        path: "payment-result",
        component: PaymentResult,
      },
      {
        name: "wallet",
        path: "wallet",
        component: Wallet,
      },
      {
        name: "transactions",
        path: "transactions",
        component: Transactions,
      },
      {
        name: "completeProfile",
        path: "complete-profile",
        component: CompleteProfile,
      },
      {
        name: "support",
        path: "support",
        component: Support,
      },
    ],
  },
];

const router = new VueRouter({ routes, mode: "history" });

// validation and guards

const PROFILE_ITEMS = [
  "name",
  "vehicle",
  "barbar",
  "living_city",
  "living_ostan",
];
const profileCompleted = (user) => {
  return PROFILE_ITEMS.every((item) => user.get(item) != "-");
};

const checkUser = async function checkAndStoreUserSessionIfExist() {
  let checked = false;

  const session = getSession();
  if (!session?.token || session?.app != "driver") return checked;

  await store
    .dispatch("become_user")
    .then(() => {
      checked = true;
    })
    .catch((err) => console.log("catch become_user", err));

  return checked;
};

const authNeeded = function checkIfRoutePathNeedsUserAuth(p) {
  return p.toLowerCase().includes("/panel");
};

router.beforeEach(async (to, from, next) => {
  if (!authNeeded(from.path)) {
    store.commit("toggle_launcher", false);
  }
  if (!authNeeded(to.path)) {
    store.commit("toggle_launcher", true);
    return next();
  }

  // check user
  const user = Parse.User.current();
  if (!user || user.get("sessionToken") !== getSessionToken()) {
    if (to.name) {
      store.commit("set_redirect", to.name);
    }
    return next({ name: "login" });
  }
  if (!store.getters.isLaunched) {
    await launch();
  }
  if (profileCompleted(user) || to.name == "completeProfile") {
    return next();
  }

  next({ name: "completeProfile" });
});

export default router;
